/* eslint-disable camelcase */
/**
 * Top part of header, shows progress of web-app according current screen,
 * is changing by completing screens, allows to move from screen to screen.
 */
import { LitElement, customElement, html } from 'lit-element';

import {
  B_FLOW_ID,
  C_B_FLOW_ID,
  C_FLOW_ID,
  C_PL_FLOW_ID,
  PL_B_FLOW_ID,
  PL_FLOW_ID,
  TRIPLE_FLOW_ID,
} from '../boilerplate/item-ids';

/**
 * LitElement
 * A simple base class for creating fast, lightweight web components.
 *
 * @docs https://lit-element.polymer-project.org/
 */
@customElement('custom-progress-indicator')
class CustomProgressIndicatorComponent extends LitElement {
  static get properties() {
    return {
      items: { type: Array },
      content_key: { type: Object },
      title: { type: String },
      completion_screen: { type: String },
      click_screen: { type: String },
      complete: { type: Boolean },
      active: { type: Boolean },
      memoizedComplete: { type: Number },
      icon: { type: String },
    };
  }

  constructor() {
    super();
    this.items = [];
    this.content_key = {};
    this.title = '';
    this.completion_screen = '';
    this.click_screen = '';
    this.active = false;
    this.complete = false;
    this.memoizedComplete = -1;
    this.icon = '';

    this.routeChangeEvent = (kasko) => this.routeChanged(kasko);
  }

  render() {
    return html`
      <div class="nav-progress-indicator">
        ${this.items.map(
          (item) => html`
          <div class="nav-progress-indicator-item"
            ?active="${item.active}"
            ?complete="${item.complete}"
            @click=${() => this.openRoute(item)}>
            <div class="line"></div>
            <div class="nav-progress-indicator-item-icon">${this.returnAsHtml(item.icon)}</div>
            <p>${this.kasko.getTranslation(item.content_key.title)}</p>
            <div class="line"></div>
          </div>
        `,
        )}
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    const { url } = this.kasko.getState('route').state;

    this.items =
      url === '/access-pin' || url.includes('summary') || this.allFlowsFullyCompleted()
        ? this.items.map((item, index) => ({
            ...item,
            active: index === 2,
            complete: index < 2,
          }))
        : this.items.map((item, index) => ({
            ...item,
            active: index === 0,
            complete: false,
          }));
    this.kasko.addEventListener('route-changed', this.routeChangeEvent);
  }

  disconnectedCallback() {
    this.kasko.removeEventListener('route-changed', this.routeChangeEvent);
    super.disconnectedCallback();
  }

  routeChanged(kasko) {
    this.normalizeItems(kasko.current?.path);
    this.setActiveItem();
  }

  normalizeItems(path) {
    if (path === 'access-pin') {
      this.memoizedComplete = 1;
    } else {
      this.items.forEach((item, index) => {
        if (
          (path === item.completion_screen || item.completion_screen.includes(path)) &&
          index > this.memoizedComplete
        ) {
          this.memoizedComplete = index;
        }
      });
    }

    return this.items.map((_item, index) => {
      const item = _item;
      item.active = item.complete ? false : index === 0;
      item.complete = this.memoizedComplete === index;
      return item;
    });
  }

  setActiveItem() {
    if (!this.items.find(({ complete }) => complete)) {
      return this.items.map((row, i) => ({
        ...row,
        active: i === 0,
      }));
    }

    let completeFound = false;
    let activeFound = false;

    return this.items.map((_item) => {
      const item = _item;
      if (completeFound && !activeFound) {
        item.active = true;
        activeFound = true;
        return item;
      }

      if (item.complete) {
        completeFound = true;
      }

      if (!completeFound) {
        item.active = false;
        item.complete = true;
      }

      return item;
    });
  }

  openRoute(item) {
    if (!item.complete && !item.active) {
      return;
    }
    if (item.click_screen === 'current') {
      return;
    }
    this.kasko.dispatchEvent('go-to-screen', { screen: item.click_screen });
  }

  returnAsHtml(str) {
    return document.createRange().createContextualFragment(`${str}`);
  }

  createRenderRoot() {
    return this;
  }

  /**
   * @returns {boolean}
   */
  allFlowsFullyCompleted() {
    const { item_id, contents_finished, private_liability_finished, buildings_finished } =
      this.kasko.getState('input');

    if (item_id === C_FLOW_ID) {
      return !!contents_finished;
    }

    if (item_id === PL_FLOW_ID) {
      return !!private_liability_finished;
    }

    if (item_id === B_FLOW_ID) {
      return !!buildings_finished;
    }

    if (item_id === C_PL_FLOW_ID) {
      return !!contents_finished && !!private_liability_finished;
    }

    if (item_id === C_B_FLOW_ID) {
      return !!contents_finished && !!buildings_finished;
    }

    if (item_id === PL_B_FLOW_ID) {
      return !!private_liability_finished && !!buildings_finished;
    }

    if (item_id === TRIPLE_FLOW_ID) {
      return !!contents_finished && !!private_liability_finished && !!buildings_finished;
    }

    return false;
  }
}

export default CustomProgressIndicatorComponent;
